/**
 * Contrato Zod — bloques interactivos de Humanidades (Lenguaje / Historia).
 */

import { z } from 'zod';
import { isInvalidSourceQuote, isInvalidSourceReference } from './sourceAnalysisGuards';
import { PayloadMetaFieldSchema } from './blockPayloadMeta';

const StructuralBreakdownLineSchema = z.object({
  text: z.string().trim().min(1, 'line text required'),
  badge: z
    .string()
    .trim()
    .min(1, 'line badge required')
    .describe(
      'El texto de la etiqueta debe ser limpio. PROHIBIDO usar sintaxis markdown (como ** o _ ) dentro de este campo.',
    ),
});

export const StructuralBreakdownPayloadSchema = z
  .object({
    title: z.string().trim().optional(),
    lines: z.array(StructuralBreakdownLineSchema).min(1, 'structural_breakdown requires lines'),
    typography: z.enum(['serif', 'sans']).optional(),
    explanation: z
      .string()
      .trim()
      .max(800)
      .optional()
      .describe(
        'Explicación didáctica breve: qué enseña el desglose y cómo leer badges/líneas. No sustituye lines.',
      ),
    whyItMatters: z
      .string()
      .trim()
      .max(500)
      .optional()
      .describe('Por qué importa este análisis para el estudiante (opcional).'),
  })
  .merge(PayloadMetaFieldSchema);

export const StructuralBreakdownBlockSchema = z.object({
  mode: z.literal('structural_breakdown'),
  title: z.string().trim().optional(),
  lines: z.array(StructuralBreakdownLineSchema).min(1),
  typography: z.enum(['serif', 'sans']).optional(),
  explanation: z.string().trim().max(800).optional(),
  whyItMatters: z.string().trim().max(500).optional(),
});

const SourceAnalysisSourceSchema = z.object({
  quote: z
    .string()
    .trim()
    .min(24, 'source quote too short')
    .describe(
      'Cita textual directa entre comillas. PROHIBIDO parafrasear ("El autor dijo que...") o placeholders.',
    )
    .refine((q) => !isInvalidSourceQuote(q), 'source quote must be direct citation, not paraphrase or placeholder'),
  reference: z
    .string()
    .trim()
    .min(4, 'source reference required')
    .refine((r) => !isInvalidSourceReference(r), 'source reference must not be template placeholder'),
  imageUrl: z.string().url().optional(),
  wikiQuery: z.string().trim().min(2).optional(),
  sourceUrl: z.string().url().optional(),
  provenance: z.string().trim().max(64).optional(),
});

export const SourceAnalysisPayloadSchema = z
  .object({
    title: z.string().trim().optional(),
    source: SourceAnalysisSourceSchema,
    questions: z
      .array(z.string().trim().min(1))
      .min(2, 'source_analysis requires at least 2 questions')
      .max(3, 'source_analysis allows at most 3 questions'),
    imageUrl: z.string().url().optional(),
    wikiQuery: z.string().trim().min(2).optional(),
  })
  .merge(PayloadMetaFieldSchema);

export const SourceAnalysisBlockSchema = z.object({
  mode: z.literal('source_analysis'),
  title: z.string().trim().optional(),
  source: SourceAnalysisSourceSchema,
  questions: z.array(z.string().trim().min(1)).min(2).max(3),
});

/** Unión discriminada para validar bloques LLM de humanidades por mode. */
export const HumanitiesInteractiveBlockSchema = z.discriminatedUnion('mode', [
  StructuralBreakdownBlockSchema,
  SourceAnalysisBlockSchema,
]);

export type HumanitiesInteractiveBlock = z.infer<typeof HumanitiesInteractiveBlockSchema>;

export function parseHumanitiesInteractiveBlock(raw: unknown) {
  return HumanitiesInteractiveBlockSchema.safeParse(raw);
}

export function validateStructuralBreakdownPayload(raw: unknown) {
  const data = StructuralBreakdownPayloadSchema.parse(raw);
  const explanation =
    String(data.explanation || '').trim() || String(data.whyItMatters || '').trim() || undefined;
  const whyItMatters = String(data.whyItMatters || '').trim() || undefined;
  return {
    ...data,
    ...(explanation ? { explanation } : {}),
    ...(whyItMatters ? { whyItMatters } : {}),
  };
}

export function validateSourceAnalysisPayload(raw: unknown) {
  return SourceAnalysisPayloadSchema.parse(raw);
}
